/* ============================================================================
   The plan catalogue — browser mirror.

   Canonical copy: `encore-functions/functions/src/plans.ts`. Swift mirror:
   `Encore/Subscriptions/Plan.swift`. Same relationship `models.ts` has with
   the Swift structs — three copies of one contract, kept in step by hand.

   If you change a number, change it in all three. Better: change the
   `plans/{planId}` document, which overrides all three at runtime with no
   release on any platform. That is the entire point of this design.

   Entitlements are DATA. Nothing here or anywhere asks "is this user on Pro";
   it asks the resolved plan whether it permits the thing.
   ============================================================================ */

/** Capabilities a plan can grant. Absent from the list means not permitted. */
export type FeatureKey =
  | 'production_docs'
  | 'calendar_export'
  | 'duplicate_tour'
  | 'venue_autofill'
  | 'shared_venue_library'
  | 'tour_books'
  | 'roster_view'
  | 'artist_profiles'
  | 'per_artist_permissions'
  | 'sso'

/** What an export is allowed to look like. */
export type ExportBranding = 'none' | 'custom' | 'whitelabel'

export interface Plan {
  id: string
  displayName: string
  tagline: string
  /**
   * Ordering for "this account somehow holds two subscriptions, grant the
   * better one". Never a feature test — comparing ranks to decide whether
   * something is allowed is the conditional this file exists to remove.
   */
  rank: number

  /** `null` means unlimited, everywhere in this file. */
  maxActiveTours: number | null
  maxEditorSeats: number | null
  /** Always null. Crew invites are the acquisition loop, so viewer seats are
   *  uncapped on every tier including free. */
  maxViewerSeats: null
  maxArtists: number | null

  exportWatermark: boolean
  exportBranding: ExportBranding
  features: FeatureKey[]

  /** Whether this plan may appear in an Apple purchase surface. Presentation
   *  only — an account already on it resolves every entitlement regardless. */
  sellableOnNative: boolean
  productIds: string[]
}

export const FREE_PLAN_ID = 'solo'

const PRO_FEATURES: FeatureKey[] = [
  'production_docs',
  'calendar_export',
  'duplicate_tour',
  'venue_autofill',
  'tour_books',
]

const AGENCY_FEATURES: FeatureKey[] = [
  ...PRO_FEATURES,
  'shared_venue_library',
  'roster_view',
  'artist_profiles',
  'per_artist_permissions',
]

/**
 * The built-in catalogue.
 *
 * Not belt-and-braces — it is what makes the Firestore layer safe. A plan
 * collection that must be seeded before the app works is one that takes the
 * product down when a document is deleted. With defaults, the worst case is
 * stale pricing rather than a locked-out account.
 */
export const DEFAULT_PLANS: Plan[] = [
  {
    id: 'solo',
    displayName: 'Solo',
    tagline: 'Everything you need for your first run.',
    rank: 0,
    maxActiveTours: 1,
    maxEditorSeats: 1,
    maxViewerSeats: null,
    maxArtists: 1,
    // The only tier that carries a watermark. Full quality otherwise — these
    // documents go to venues and promoters, so they are a distribution
    // channel, not a crippled sample.
    exportWatermark: true,
    exportBranding: 'none',
    features: [],
    sellableOnNative: false,
    productIds: [],
  },
  {
    id: 'single_tour',
    displayName: 'Single Tour',
    tagline: 'One run, start to finish.',
    rank: 1,
    maxActiveTours: 1,
    maxEditorSeats: 3,
    maxViewerSeats: null,
    maxArtists: 1,
    exportWatermark: false,
    exportBranding: 'none',
    features: ['production_docs', 'calendar_export'],
    sellableOnNative: true,
    productIds: ['encore.singletour'],
  },
  {
    id: 'pro',
    displayName: 'Pro',
    tagline: 'For artists touring properly.',
    rank: 2,
    maxActiveTours: null,
    maxEditorSeats: null,
    maxViewerSeats: null,
    maxArtists: 1,
    exportWatermark: false,
    exportBranding: 'none',
    features: PRO_FEATURES,
    sellableOnNative: true,
    productIds: ['encore.pro.monthly', 'encore.pro.yearly'],
  },
  {
    id: 'agency5',
    displayName: 'Agency',
    tagline: 'For teams looking after a roster.',
    rank: 3,
    maxActiveTours: null,
    maxEditorSeats: null,
    maxViewerSeats: null,
    maxArtists: 5,
    exportWatermark: false,
    exportBranding: 'custom',
    features: AGENCY_FEATURES,
    sellableOnNative: true,
    productIds: ['encore.agency5.monthly', 'encore.agency5.yearly'],
  },
  {
    id: 'agency15',
    displayName: 'Agency',
    tagline: 'For teams looking after a roster.',
    rank: 4,
    maxActiveTours: null,
    maxEditorSeats: null,
    maxViewerSeats: null,
    maxArtists: 15,
    exportWatermark: false,
    exportBranding: 'custom',
    features: AGENCY_FEATURES,
    sellableOnNative: true,
    productIds: ['encore.agency15.monthly', 'encore.agency15.yearly'],
  },
  {
    id: 'agency_pro',
    displayName: 'Agency Pro',
    tagline: 'For agencies at full tilt.',
    rank: 5,
    maxActiveTours: null,
    maxEditorSeats: null,
    maxViewerSeats: null,
    maxArtists: null,
    exportWatermark: false,
    exportBranding: 'whitelabel',
    features: [...AGENCY_FEATURES, 'sso'],
    // Web and sales conversations only. No StoreKit product exists for it.
    sellableOnNative: false,
    productIds: [],
  },
]

export const FREE_PLAN: Plan = DEFAULT_PLANS.find((plan) => plan.id === FREE_PLAN_ID)!

export function defaultPlan(planId: string): Plan | undefined {
  return DEFAULT_PLANS.find((plan) => plan.id === planId)
}

/**
 * Merges a Firestore document over its built-in default, field by field.
 *
 * A missing key means "not overridden", never "none allowed" — a document that
 * omits `maxActiveTours` must not silently cap someone at zero tours. `null`
 * is meaningful and different: it is how unlimited is written.
 */
export function mergePlan(id: string, data: Record<string, unknown>): Plan {
  const base = defaultPlan(id) ?? FREE_PLAN
  const pick = <K extends keyof Plan>(key: K): Plan[K] =>
    key in data ? (data[key as string] as Plan[K]) : base[key]

  return {
    ...base,
    id,
    displayName: pick('displayName'),
    tagline: pick('tagline'),
    rank: pick('rank'),
    maxActiveTours: pick('maxActiveTours'),
    maxEditorSeats: pick('maxEditorSeats'),
    maxViewerSeats: null,
    maxArtists: pick('maxArtists'),
    exportWatermark: pick('exportWatermark'),
    exportBranding: pick('exportBranding'),
    features: pick('features'),
    sellableOnNative: pick('sellableOnNative'),
    productIds: pick('productIds'),
  }
}

/* ------------------------------------------------------------- subscription */

/**
 * States a subscription can be in.
 *
 * `grace` is separate from `active` so the UI can nudge about a failed payment
 * without the entitlement layer treating the account as lapsed. Apple retries
 * for days; cutting access on day one of a retry turns an expired card into a
 * cancellation.
 */
export type SubscriptionStatus = 'active' | 'grace' | 'paused' | 'expired' | 'refunded'

/**
 * What the server records about an account's billing.
 *
 * Written exclusively by the App Store Server Notifications handler, the
 * Stripe webhook, and manual admin grants. The security rules reject every
 * client write to it — a value the client can set is one it can forge.
 */
export interface SubscriptionRecord {
  /** `manual` is comps and sales deals, and is also how a plan is assigned
   *  before any billing exists at all. */
  source: 'apple' | 'web' | 'manual'
  externalId?: string | null
  productId?: string | null
  planId: string
  status: SubscriptionStatus
  /** Millis, or a Firestore Timestamp. Null for a non-expiring manual grant. */
  currentPeriodEnd?: number | { toMillis(): number } | null
  graceUntil?: number | { toMillis(): number } | null
}

/**
 * An admin grant that sits ALONGSIDE billing rather than replacing it.
 *
 * A comp written into the subscription record would be overwritten the next
 * time a webhook fired — Apple and Stripe own that field. An override is a
 * separate field nothing but an admin touches, and resolution takes whichever
 * of the two grants more. Clearing it drops the account back to exactly what
 * they pay for, with nothing to reconstruct.
 */
export interface PlanOverride {
  planId: string
  /** Millis. Null never expires — staff accounts and lifetime comps. */
  expiresAt?: number | null
  note?: string
  setBy?: string
  setAt?: number
}

/** Pause is read-only-plus, not read-nothing — but it does drop to free. */
const ENTITLING: SubscriptionStatus[] = ['active', 'grace']

function millis(value: SubscriptionRecord['graceUntil']): number | null {
  if (value === null || value === undefined) return null
  return typeof value === 'number' ? value : value.toMillis()
}

/**
 * Which plan an account is on right now.
 *
 * Never branches on `source`. That is the point — a subscription bought on an
 * iPhone must resolve identically here.
 */
export function resolvePlanId(
  subscription: SubscriptionRecord | null | undefined,
  now: number = Date.now(),
): string {
  if (!subscription) return FREE_PLAN_ID
  if (!ENTITLING.includes(subscription.status)) return FREE_PLAN_ID

  const until = millis(subscription.graceUntil) ?? millis(subscription.currentPeriodEnd)
  if (until !== null && until < now) return FREE_PLAN_ID

  return subscription.planId
}

/** The override's plan, if it hasn't lapsed. */
export function resolveOverridePlanId(
  override: PlanOverride | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!override) return null
  if (override.expiresAt != null && override.expiresAt < now) return null
  return override.planId
}

/**
 * The plan an account is actually on: the better of what they pay for and what
 * an admin granted them.
 *
 * Never the lower of the two. An override exists to give someone more — using
 * it to take entitlements away would let an admin silently downgrade a paying
 * customer, and the refund conversation that follows is not one anyone wants.
 */
export function effectivePlanId(
  subscription: SubscriptionRecord | null | undefined,
  override: PlanOverride | null | undefined,
  now: number = Date.now(),
): string {
  const ids = [resolvePlanId(subscription, now), resolveOverridePlanId(override, now)]
    .filter((id): id is string => id !== null)
  const plans = ids.map(defaultPlan).filter((plan): plan is Plan => plan !== undefined)
  if (plans.length === 0) return FREE_PLAN_ID
  return plans.reduce((best, plan) => (plan.rank > best.rank ? plan : best)).id
}
